// Service Worker Manager - Register and manage Service Worker
// Netflix/Duolingo-style offline-first architecture

import { logger } from '../utils/logger';

class ServiceWorkerManager {
  private registration: ServiceWorkerRegistration | null = null;
  private isSupported: boolean = 'serviceWorker' in navigator;

  /**
   * Register Service Worker
   */
  async register(): Promise<boolean> {
    if (!this.isSupported) {
      logger.warn('Service Worker not supported in this browser');
      return false;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      logger.info('Service Worker registered:', this.registration.scope);

      // Listen for updates and seamlessly activate them.
      //
      // When a new SW is installed AND there's already an active controller,
      // it means a deploy has shipped fresh assets. Tell the new SW to skip the
      // `waiting` phase and take over immediately. The page-level
      // `controllerchange` listener (see serviceWorkerRegistration.ts) then
      // reloads once so the user picks up the new HTML/CSS/JS. Without this,
      // the new SW would sit in `waiting` until every tab is closed — which on
      // an installed PWA effectively means "until the user uninstalls the app".
      this.registration.addEventListener('updatefound', () => {
        const newWorker = this.registration?.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.info('New Service Worker installed — activating');
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        }
      });

      // Check for updates periodically
      setInterval(() => {
        this.registration?.update();
      }, 60000); // Check every minute

      return true;
    } catch (error) {
      logger.error('Service Worker registration failed:', error);
      return false;
    }
  }

  /**
   * Unregister Service Worker
   */
  async unregister(): Promise<boolean> {
    if (!this.registration) {
      return false;
    }

    try {
      const unregistered = await this.registration.unregister();
      if (unregistered) {
        logger.info('Service Worker unregistered');
        this.registration = null;
      }
      return unregistered;
    } catch (error) {
      logger.error('Service Worker unregistration failed:', error);
      return false;
    }
  }

  /**
   * Cache questions for offline use
   */
  async cacheQuestions(testId: string, language: string, questions: any[]): Promise<void> {
    if (!this.registration || !this.registration.active) {
      return;
    }

    try {
      this.registration.active.postMessage({
        type: 'CACHE_QUESTIONS',
        testId,
        language,
        questions
      });
      logger.debug('Cached questions in Service Worker:', testId, language);
    } catch (error) {
      logger.error('Error caching questions in Service Worker:', error);
    }
  }

  /**
   * Clear all caches
   */
  async clearCache(): Promise<void> {
    if (!this.registration || !this.registration.active) {
      return;
    }

    try {
      this.registration.active.postMessage({
        type: 'CLEAR_CACHE'
      });
      logger.info('Cleared Service Worker cache');
    } catch (error) {
      logger.error('Error clearing Service Worker cache:', error);
    }
  }

  /**
   * Check if Service Worker is active
   */
  isActive(): boolean {
    return this.registration?.active !== null && this.registration?.active !== undefined;
  }

  /**
   * Get Service Worker registration
   */
  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }
}

export const serviceWorkerManager = new ServiceWorkerManager();






















